import React, { useState, useEffect } from "react" 

import { withAuthenticator } from 'aws-amplify-react'
import Amplify, { API, graphqlOperation, Storage, Analytics } from 'aws-amplify'
import Loader from 'react-loader-spinner'

import { inspectionsByUser, listVehicles, getUser, listCustomInspections } from '../../graphql/queries';
import amplifyConfig, { amplifyConfigWarnings } from '../../config/amplify'
import AWSDataContext from './context'

import { generateQR, generatePDF} from "./generate"
import { 
    createNewVehicle, 
    editVehicle, 
    deleteCurrentVehicle, 
    createNewType,
    editType, 
    deleteSelectedType
} from "./mutate"

import { getStatus } from "../../util"

Amplify.configure(amplifyConfig);

Analytics.disable()

amplifyConfigWarnings.forEach((warning) => {
    console.warn(warning)
})

const LIST_PAGE_SIZE = 250
const DEFAULT_USER = {
    tier: '',
    daysLeft: 'Unavailable',
    vehiclesLeft: 0,
    inspectionsLeft: 0,
}

const DEFAULT_INSPECTION_METADATA = {
    name: '',
    location: '',
    type: '',
    operatorName: '',
    signature: '',
}

function createDefaultCustomDict() {
    return {
        "001" : { id: "0", fallback: "001", type: "Loader", items: ""},
        "002" : { id: "0", fallback: "002", type: "Excavator", items: ""},
        "003" : { id: "0", fallback: "003", type: "Rock Truck", items: ""},
        "004" : { id: "0", fallback: "004", type: "Crane", items: ""},
        "005" : { id: "0", fallback: "005", type: "Pickup Truck", items: ""},
        "006" : { id: "0", fallback: "006", type: "Towed Trailer", items: ""},
        "007" : { id: "0", fallback: "007", type: "General", items: ""},
        "008" : { id: "0", fallback: "008", type: "Single Unit Truck", items: ""},
        "009" : { id: "0", fallback: "009", type: "Small Passenger Vehicle", items: ""},
        "010" : { id: "0", fallback: "010", type: "School Bus", items: ""},
        "011" : { id: "0", fallback: "011", type: "General Tractor", items: ""},
        "012" : { id: "0", fallback: "012", type: "Motor Coach", items: ""},
        "013" : { id: "0", fallback: "013", type: "Mower", items: ""},
    }
}

function createInitialSliceStatus() {
    return {
        user: 'loading',
        vehicles: 'loading',
        inspections: 'loading',
        customInspections: 'loading',
    }
}

function pluralize(count, singular, plural = `${singular}s`) {
    return count === 1 ? singular : plural
}

function tryParseJson(value) {
    if (value == null) {
        return { ok: false, value: null }
    }

    if (typeof value === 'object') {
        return { ok: true, value }
    }

    if (typeof value !== 'string') {
        return { ok: false, value: null }
    }

    try {
        return { ok: true, value: JSON.parse(value) }
    } catch (error) {
        return { ok: false, value: null }
    }
}

function parseStoredPayload(value) {
    const parsed = tryParseJson(value)
    if (!parsed.ok) {
        return parsed
    }

    if (typeof parsed.value === 'string') {
        return tryParseJson(parsed.value)
    }

    return parsed
}

function buildLoadMessages(sliceIssues, parseIssues) {
    const messages = [...sliceIssues]

    if (parseIssues.skippedVehicles > 0) {
        messages.push(
            `Skipped ${parseIssues.skippedVehicles} malformed ${pluralize(parseIssues.skippedVehicles, 'vehicle record')}.`,
        )
    }

    if (parseIssues.skippedCustomInspections > 0) {
        messages.push(
            `Skipped ${parseIssues.skippedCustomInspections} malformed ${pluralize(parseIssues.skippedCustomInspections, 'custom inspection template')}.`,
        )
    }

    if (parseIssues.inspectionsWithFallbackData > 0) {
        messages.push(
            `Loaded ${parseIssues.inspectionsWithFallbackData} ${pluralize(parseIssues.inspectionsWithFallbackData, 'inspection record')} with partial fallback data because stored payloads were malformed.`,
        )
    }

    if (parseIssues.orphanedInspections > 0) {
        messages.push(
            `Hidden ${parseIssues.orphanedInspections} ${pluralize(parseIssues.orphanedInspections, 'inspection record')} because ${parseIssues.orphanedInspections === 1 ? 'its vehicle record is' : 'their vehicle records are'} missing or deleted.`,
        )
    }

    return messages
}

function getLoadingLabel(sliceStatus) {
    const pending = []

    if (sliceStatus.user === 'loading') {
        pending.push('account limits')
    }

    if (sliceStatus.vehicles === 'loading') {
        pending.push('vehicles')
    }

    if (sliceStatus.inspections === 'loading') {
        pending.push('inspection history')
    }

    if (sliceStatus.customInspections === 'loading') {
        pending.push('custom inspections')
    }

    if (pending.length === 0) {
        return 'Loading account data...'
    }

    if (pending.length === 1) {
        return `Loading ${pending[0]}...`
    }

    return `Loading ${pending.slice(0, -1).join(', ')} and ${pending[pending.length - 1]}...`
}

function getBlockingLoadError(pathname, sliceStatus) {
    const routeRequiresVehicles = [
        '/vehicle',
        '/editvehicle',
        '/notificationvehicle',
        '/audits',
        '/qrcodes',
    ].includes(pathname)
    const routeRequiresInspections = ['/inspection', '/audits'].includes(pathname)

    if (routeRequiresVehicles && sliceStatus.vehicles === 'error') {
        return 'Vehicle records are unavailable right now, so this page cannot be shown safely.'
    }

    if (routeRequiresInspections && sliceStatus.inspections === 'error') {
        return 'Inspection history is unavailable right now, so this page cannot be shown safely.'
    }

    return ''
}

function normalizePhotoKey(key) {
    if (typeof key !== 'string') {
        return ''
    }

    const trimmedKey = key.trim()

    if (!trimmedKey) {
        return ''
    }

    if (trimmedKey.startsWith('data:') || /^https?:\/\//i.test(trimmedKey)) {
        return trimmedKey
    }

    if (trimmedKey.startsWith('public/')) {
        return trimmedKey.slice(7)
    }

    return trimmedKey
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result)
                return
            }

            reject(new Error('Unable to convert image blob into a data URL.'))
        }
        reader.onerror = () => reject(reader.error || new Error('Unable to read image blob.'))
        reader.readAsDataURL(blob)
    })
}

async function getPhoto(key) {
    const normalizedKey = normalizePhotoKey(key)

    if (!normalizedKey) {
        return null
    }

    if (normalizedKey.startsWith('data:')) {
        return normalizedKey
    }

    try {
        const photoUrl = /^https?:\/\//i.test(normalizedKey)
            ? normalizedKey
            : await Storage.get(normalizedKey)
        const response = await fetch(photoUrl)

        if (!response.ok) {
            console.warn(`Photo could not be loaded for key "${normalizedKey}".`, response.status)
            return null
        }

        const blob = await response.blob()
        return await blobToDataUrl(blob)
    } catch (error) {
        console.warn(`Photo could not be resolved for key "${normalizedKey}".`, error)
        return null
    }
}

async function fetchAllPages(query, dataKey, variables) {
    const all = []
    let nextToken = null

    do {
        const result = await API.graphql(graphqlOperation(query, {
            ...variables,
            limit: LIST_PAGE_SIZE,
            nextToken,
        }))
        const page = result && result.data ? result.data[dataKey] : null

        if (!page || !Array.isArray(page.items)) {
            throw new Error(`Invalid ${dataKey} response`)
        }

        all.push(...page.items.filter(Boolean))
        nextToken = page.nextToken || null
    } while (nextToken)

    return all
}

async function fetchAllInspectionsForUser(username) {
    return fetchAllPages(inspectionsByUser, 'inspectionsByUser', {
        uId: username,
    })
}

async function fetchVehiclesForUser(username) {
    return fetchAllPages(listVehicles, 'listVehicles', {
        filter: { uId: { eq: username } },
    })
}

async function fetchCustomInspectionsForUser(username) {
    return fetchAllPages(listCustomInspections, 'listCustomInspections', {
        filter: { uId: { eq: username } },
    })
}

async function fetchUserRecord(username) {
    const result = await API.graphql(graphqlOperation(getUser, { id: username }))
    return result && result.data ? result.data.getUser : null
}

function generateUser(user, vehicleCounter, inspectionCounter) {
    if (!user) {
        return DEFAULT_USER
    }

    const today = new Date();
    const createdDate = new Date(user.dateCreated)
    const hasValidCreatedDate = !Number.isNaN(createdDate.getTime())
    let daysLeft = 'Unavailable'

    if (hasValidCreatedDate) {
        const expiryDate = new Date(createdDate)
        expiryDate.setDate(expiryDate.getDate() + 31)
        daysLeft = Math.max(0, Math.ceil((expiryDate - today)/(60*60*24*1000)))
        daysLeft = (daysLeft === 0) ? "Expired, Please Contact Us!" : daysLeft
    }

    return {
        tier: user.tier || '',
        daysLeft,
        vehiclesLeft: Math.max(0, Number(user.vehiclesLimit || 0) - vehicleCounter),
        inspectionsLeft: Math.max(0, Number(user.inspectionsLimit || 0) - inspectionCounter),
    }
}

function generateDicts(vehicles, inspections, customInspections) {
    const parseIssues = {
        skippedVehicles: 0,
        skippedCustomInspections: 0,
        inspectionsWithFallbackData: 0,
        orphanedInspections: 0,
    }
    const customDict = createDefaultCustomDict()

    for (let customInd = 0; customInd < customInspections.length; customInd++) {
        const custom = customInspections[customInd]
        const parsedItems = tryParseJson(custom.items)

        if (!parsedItems.ok) {
            parseIssues.skippedCustomInspections += 1
            continue
        }

        customDict[custom.id] = {
            id: custom.id,
            type: custom.type,
            fallback: custom.fallback,
            items: parsedItems.value,
        }
    }

    const vehicleDict = {}
    for (let vehicleInd = 0; vehicleInd < vehicles.length; vehicleInd++) {
        const vehicle = vehicles[vehicleInd]
        const parsedVehicleData = tryParseJson(vehicle.data)

        if (!parsedVehicleData.ok || !parsedVehicleData.value || typeof parsedVehicleData.value !== 'object') {
            parseIssues.skippedVehicles += 1
            continue
        }

        vehicleDict[vehicle.id] = {
            id: vehicle.id,
            vehicleData: parsedVehicleData.value,
            UTCDate: "",
            lastDate: "",
            lastStatus: "",
            lastOdometer: "",
            lastDone: "",
            inspections: [],
        }
    }

    const inspectionDict = {}

    for (let inspectionInd = 0; inspectionInd < inspections.length; inspectionInd++) {
        const inspection = inspections[inspectionInd]
        const vehicleEntry = vehicleDict[inspection.vId]

        if (!vehicleEntry) {
            parseIssues.orphanedInspections += 1
            continue
        }

        const insDate = new Date(inspection.date)
        const hasValidDate = !Number.isNaN(insDate.getTime())
        const fDate = hasValidDate ? insDate.toLocaleString().slice(0,10) : ''
        const fDateTime = hasValidDate ? `${fDate}-${insDate.toTimeString().slice(0, 5)}` : ''
        const metadataPayload = parseStoredPayload(inspection.metadata)
        const itemsPayload = parseStoredPayload(inspection.items)
        let inspectionUsedFallbackData = false

        const metadata = metadataPayload.ok && metadataPayload.value && typeof metadataPayload.value === 'object'
            ? {
                ...DEFAULT_INSPECTION_METADATA,
                ...(metadataPayload.value.metadata && typeof metadataPayload.value.metadata === 'object'
                    ? metadataPayload.value.metadata
                    : metadataPayload.value),
            }
            : (() => {
                inspectionUsedFallbackData = true
                return { ...DEFAULT_INSPECTION_METADATA }
            })()

        const items = itemsPayload.ok && Array.isArray(itemsPayload.value)
            ? itemsPayload.value
            : (() => {
                inspectionUsedFallbackData = true
                return []
            })()

        if (inspectionUsedFallbackData) {
            parseIssues.inspectionsWithFallbackData += 1
        }

        if (inspection.date > vehicleEntry.UTCDate) {
            vehicleEntry.UTCDate = inspection.date
            vehicleEntry.lastDate = fDate
            vehicleEntry.lastOdometer = inspection.odometer + "km"
            vehicleEntry.lastStatus = getStatus(inspection.status)

            if (hasValidDate) {
                const today = new Date()
                const lastDone = Math.ceil((today - insDate)/(1000*3600*24))
                vehicleEntry.lastDone = lastDone + " Days Ago"
            }
        }

        const inspectionRecord = {
            id: inspection.id,
            vId: inspection.vId,
            name: fDate,
            status: getStatus(inspection.status),
            statusCode: inspection.status,
            UTCdate: inspection.date,
            date: fDateTime,
            vehicleData: vehicleEntry.vehicleData,
            vehicleType: vehicleEntry.vehicleData.type,
            odometer: inspection.odometer + "km",
            odometerRaw: inspection.odometer,
            metadata,
            items,
        }

        vehicleEntry.inspections.push(inspectionRecord)
        inspectionDict[inspection.id] = inspectionRecord
    }

    return {
        dicts: {inspectionDict, vehicleDict, customDict},
        parseIssues,
    }
}

async function fetchInspections(username, nextToken, pInspections) {
    // retrieveRawData now drains all pages via the inspectionsByUser GSI query,
    // so there is nothing left to load. Returning done:true tells the UI to hide
    // any "Load more" affordance.
    return { rInspections: pInspections, nt: null, done: true };
}

function AWSData({ location, children, authData, ...rest }) {
    const [rawUser, setRawUser] = useState(null)
    const [sliceStatus, setSliceStatus] = useState(createInitialSliceStatus())
    const [sliceIssues, setSliceIssues] = useState([])
	const [retryCount, setRetryCount] = useState(0)
    const [uId, setUId] = useState(authData.username)
    const [inspections, setInspections] = useState([])
    const [vehicles, setVehicles] = useState([])
    const [customInspections, setCustomInspections] = useState([])
    const [nextToken, setNextToken] = useState(null)
    const [done, setDone] = useState(false)

    const search = location.search.slice(1)
    const { dicts, parseIssues } = generateDicts(vehicles, inspections, customInspections)
    const user = generateUser(rawUser, vehicles.length, inspections.length)
    const loadMessages = buildLoadMessages(sliceIssues, parseIssues)
    const isLoading = Object.values(sliceStatus).some((status) => status === 'loading')
    const allSlicesFailed = Object.values(sliceStatus).every((status) => status === 'error')
    const blockingLoadError = getBlockingLoadError(location.pathname, sliceStatus)

	useEffect(() => {
        let isActive = true

        setRawUser(null)
        setVehicles([])
        setInspections([])
        setCustomInspections([])
        setUId(authData.username)
        setNextToken(null)
        setDone(false)
        setSliceIssues([])
        setSliceStatus(createInitialSliceStatus())

        const loadSlice = async (key, loader, onSuccess, issueMessage) => {
            try {
                const result = await loader()

                if (!isActive) {
                    return
                }

                onSuccess(result)
                setSliceStatus((prevState) => ({ ...prevState, [key]: 'ready' }))
            } catch (error) {
                console.log(error)
                if (!isActive) {
                    return
                }

                setSliceStatus((prevState) => ({ ...prevState, [key]: 'error' }))
                setSliceIssues((prevState) => prevState.concat(issueMessage))
            }
        }

        loadSlice(
            'user',
            () => fetchUserRecord(authData.username),
            (result) => setRawUser(result),
            'Account limits are unavailable right now.',
        )

        loadSlice(
            'vehicles',
            () => fetchVehiclesForUser(authData.username),
            (result) => setVehicles(result),
            'Vehicle records could not be loaded.',
        )

        loadSlice(
            'inspections',
            () => fetchAllInspectionsForUser(authData.username),
            (result) => {
                setInspections(result)
                setDone(true)
            },
            'Inspection history could not be loaded. Vehicle details remain available.',
        )

        loadSlice(
            'customInspections',
            () => fetchCustomInspectionsForUser(authData.username),
            (result) => setCustomInspections(result),
            'Custom inspection templates could not be loaded. Default vehicle types remain available.',
        )

        return () => {
            isActive = false
        }
    }, [authData.username, retryCount])


    const childrenWithData = React.Children.map(children, (child) => {
        if (child.type) {
            return React.cloneElement(child, { 
                dicts:dicts, 
                user:user,
                done:done,
                nInsp: 0,
                fetchInspections: () => fetchInspections(authData.username, nextToken, inspections).then(
                    ({rInspections, nt, done})=> {

                        setInspections(rInspections);
                        setNextToken(nt);
                        setDone(done);
                    }).catch(err => console.log(err)),
                createVehicle: (vehicleData) => createNewVehicle(uId, vehicleData),
                editVehicle: (vehicleData) => editVehicle(search, vehicleData),
                deleteVehicle: () => deleteCurrentVehicle(search),
                createNewType: (typeDetails) => createNewType(uId, typeDetails),
                editType: (iId, typeDetails) => editType(iId, typeDetails),
                deleteSelectedType: (iId) => deleteSelectedType(iId),
                getPhoto: (key) => getPhoto(key),
                generateQR: (vIds) => generateQR(uId, dicts.vehicleDict, dicts.customDict, vIds),
                generateInspection: (
                    inspection,
                    startDate = new Date(),
                    endDate = new Date(), 
                    workorder = true,
                    onProgress,
                ) => generatePDF(inspection, dicts.customDict, startDate, endDate, workorder, (key) => getPhoto(key), onProgress),
            })
        }

        return child
    })

    if (isLoading) {
        return (
            <div style={{textAlign:"center"}}>
                <Loader
                    type="Puff"
                    color="#00BFFF"
                    height={100}
                    width={100}
                />
                <p>{getLoadingLabel(sliceStatus)}</p>
            </div>
        )
    } 

    if (allSlicesFailed) {
        return (
            <div style={{textAlign:"center"}}>
                <p>Unable to load account data. Please try again.</p>
                {loadMessages.map((message) => (
                    <p key={message}>{message}</p>
                ))}
                <button type="button" onClick={() => setRetryCount((value) => value + 1)}>
                    Retry
                </button>
            </div>
        )
    }

    if (blockingLoadError) {
        return (
            <div style={{textAlign:"center"}}>
                <p>{blockingLoadError}</p>
                {loadMessages.map((message) => (
                    <p key={message}>{message}</p>
                ))}
                <button type="button" onClick={() => setRetryCount((value) => value + 1)}>
                    Retry
                </button>
            </div>
        )
    }

    return (
        <AWSDataContext.Provider value={{ loadMessages }}>
            {childrenWithData}
        </AWSDataContext.Provider>
    )
}

export default withAuthenticator(AWSData)